// Serien-Fortschritt (08/2026 Phase 2): pollt den serverseitigen Hintergrund-Job
// (Buchen/Löschen) und zeigt "X von Y". Läuft weiter, auch nach Seiten-Reload,
// weil der Job serverseitig läuft. Mehrere Karten stapeln sich (offset).
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { X, CalendarCheck, Trash2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SeriesSyncProgress = ({ seriesId, offset = 0, onDone }) => {
  const [status, setStatus] = useState(null);
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!seriesId) return;
    setVisible(true);
    let cancelled = false;
    const poll = async () => {
      try {
        const _tok = localStorage.getItem('token');
        const res = await axios.get(`${API}/appointments/series/${seriesId}/job-status`,
          _tok ? { headers: { Authorization: `Bearer ${_tok}` } } : undefined);
        if (cancelled) return;
        setStatus(res.data);
        const st = res.data?.status;
        if (st === 'running' || st === 'idle') {
          timerRef.current = setTimeout(poll, 1500);
        } else {
          // done / error → kurz anzeigen, dann ausblenden + Kalender refreshen
          timerRef.current = setTimeout(() => {
            setVisible(false);
            onDone && onDone(seriesId, st);
          }, 5000);
        }
      } catch (e) {
        if (cancelled) return;
        // Bei Auth-Fehlern NICHT endlos weiter pollen (sonst Flut aus 403ern
        // auf der Login-Seite / nach Session-Ablauf). Poller sauber beenden.
        const _code = e?.response?.status;
        if (_code === 401 || _code === 403) {
          setVisible(false);
          return;
        }
        timerRef.current = setTimeout(poll, 3000);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [seriesId, onDone]);

  if (!seriesId || !visible || !status || status.status === 'idle') return null;

  const kind = status.kind === 'deletion' ? 'deletion' : 'booking';
  const total = status.total || 0;
  const processed = status.processed || 0;
  const isError = status.status === 'error';
  const finished = status.status === 'done';
  const pct = finished ? 100 : (total > 0 ? Math.min(99, Math.round((processed / total) * 100)) : 5);

  const barColor = isError ? 'bg-red-500' : finished ? 'bg-emerald-500' : (kind === 'deletion' ? 'bg-rose-600' : 'bg-indigo-600');
  const Icon = isError ? AlertTriangle : kind === 'deletion' ? Trash2 : CalendarCheck;
  const iconColor = isError ? 'text-red-600' : finished ? 'text-emerald-600' : (kind === 'deletion' ? 'text-rose-600' : 'text-indigo-600');
  const verb = kind === 'deletion' ? 'gelöscht' : 'gebucht';

  let title;
  if (isError) title = `Serie ${kind === 'deletion' ? 'löschen' : 'buchen'} fehlgeschlagen`;
  else if (finished) title = `Serie vollständig ${verb} ✅`;
  else title = kind === 'deletion' ? 'Termine werden gelöscht…' : 'Termine werden gebucht…';

  return (
    <div
      data-testid="series-sync-progress"
      className="fixed left-6 z-50 w-[320px] bg-white rounded-xl shadow-2xl border border-gray-200 p-4"
      style={{ bottom: `${24 + offset * 108}px` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <span className="text-sm font-semibold text-gray-800">{title}</span>
        </div>
        <button
          data-testid="series-sync-close"
          onClick={() => { setVisible(false); onDone && onDone(seriesId, status.status); }}
          className="p-0.5 rounded hover:bg-gray-100 text-gray-400"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-3 h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1.5 text-xs text-gray-500" data-testid="series-sync-count">
        {isError ? (
          <span className="text-red-600">{status.error || 'Bitte erneut versuchen.'}</span>
        ) : (
          <>
            {finished
              ? `${kind === 'deletion' ? total : (status.created_count ?? total)} Termine ${verb}`
              : `${processed}/${total} Termine ${verb} (${pct}%)`}
            {status.skipped_count > 0 && ` · ${status.skipped_count} übersprungen`}
            {!finished && ' — du kannst weiterarbeiten'}
          </>
        )}
      </p>
    </div>
  );
};

export default SeriesSyncProgress;
